/**
 * Per-method strike-selection validators.
 *
 * Single source of truth for method-specific validation RULES — the same
 * helper is called from `strategyValidators.ts` (form-level),
 * `strategyTransformationService.ts` (in-service preflight), and
 * `strategyValidationService.ts` (advanced/async validator). Keeping the
 * rules in one place prevents the 3-way drift the review flagged.
 *
 * Backend counterpart: `lambda_functions/strategy/strategy_manager.py`
 * `validate_and_enhance_legs`. The RULES match (same finite / range / decimals
 * checks) but the frontend WORDING is user-friendly ("Percentage of Underlying
 * must be between 0.05 and 10") while the backend reads more like a schema
 * error ("PERCENTAGE_OF_UNDERLYING selection_value must be between 0.05 and 10").
 * Users hitting the frontend Save button see the friendly wording; direct API
 * callers (marketplace imports, curl, retry after JS crash) see the backend
 * form.
 */

import { Leg } from "../../types/strategy";
import { UNDERLYING_PCT } from "./strategyConstants";

/**
 * Prefix each error with "prefix " if non-empty. Callers pass "Position N:"
 * (form-level, position-numbered) or "" (per-leg advanced validator that
 * attaches the leg index separately in its LegValidationResult).
 */
const withPrefix = (prefix: string, msg: string): string =>
  prefix ? `${prefix} ${msg}` : msg;

/**
 * Validate PERCENTAGE_OF_UNDERLYING configuration on a leg.
 *
 * Rules (mirrors backend):
 * - `underlyingPremiumOperator` required (CLOSEST / GTE / LTE).
 * - `underlyingPercentage` finite AND in `[MIN, MAX]`.
 * - `underlyingPercentage` has at most `DECIMALS` decimal places (float-safe
 *   check via `abs(v * scale - round(v * scale)) < 1e-9`).
 *
 * Returns a string[] of human-readable error messages, or [] if valid.
 */
export const validatePercentageOfUnderlying = (leg: Leg, prefix: string = ""): string[] => {
  const errors: string[] = [];
  if (!leg.underlyingPremiumOperator) {
    errors.push(withPrefix(prefix, "Percentage of Underlying operator is required"));
  }
  const v = leg.underlyingPercentage;
  if (v === undefined || v === null) {
    errors.push(withPrefix(prefix, "Percentage of Underlying is required"));
  } else if (!Number.isFinite(v)) {
    // Mirrors the backend's "must be a finite number" gate. Reachable when a
    // corrupt DDB row or hand-crafted import surfaces NaN / Infinity — the
    // LegMatrix input never emits these directly.
    errors.push(withPrefix(prefix, "Percentage of Underlying must be a finite number"));
  } else if (v < UNDERLYING_PCT.MIN || v > UNDERLYING_PCT.MAX) {
    errors.push(withPrefix(prefix, `Percentage of Underlying must be between ${UNDERLYING_PCT.MIN} and ${UNDERLYING_PCT.MAX}`));
  } else {
    const scale = 10 ** UNDERLYING_PCT.DECIMALS;
    if (Math.abs(v * scale - Math.round(v * scale)) >= 1e-9) {
      errors.push(withPrefix(prefix, `Percentage of Underlying can have at most ${UNDERLYING_PCT.DECIMALS} decimal places`));
    }
  }
  return errors;
};
